import React, { useMemo } from 'react';
import { Box, Ellipsis, Icon, Text } from '@inflearn/ds-react';
import { css } from '@emotion/react';
import {
  BLANK_SCHEDULE,
  EMPTY_SCHEDULE,
  NETWORK_SCHEDULE,
  PREPARE_NETWORK_SCHEDULE,
  type Schedule
} from '../../../dtos/ShareScheduleDto';
import { GATSBY_CDN_URL } from '../../../utils/env';
import { faComment } from '@fortawesome/pro-solid-svg-icons';

type Props = {
  startedAt: string;
  endedAt: string;
  schedules: Schedule[];
};

const SHARE_TABLE_CELL_COLOR = ['#3548F1', '#9D36F2', '#009DFF', '#06C36F', '#F40291', '#F9A94D'];

const ShareTableRow = ({ startedAt, endedAt, schedules }: Props) => {
  return (
    <>
      <Box css={styleShareTimeCell}>
        <Text css={styleShareTableStartedAt}>{startedAt}</Text>
        <Text css={styleShareTableEndedAt}>{endedAt}</Text>
      </Box>
      {schedules.map((schedule, index) => {
        if (schedule.title === EMPTY_SCHEDULE.title) {
          return <ShareTableEmptyCell key={index} />;
        }

        if (schedule.title === PREPARE_NETWORK_SCHEDULE.title) {
          return <ShareTablePrepareNetworkCell key={index} />;
        }

        if (schedule.title === NETWORK_SCHEDULE.title) {
          return <ShareTableNetworkCell key={index} />;
        }

        if (schedule.title === BLANK_SCHEDULE.title) {
          return '';
        }

        return <ShareTableCell schedule={schedule} index={index} key={index} />;
      })}
    </>
  );
};

type CellProps = {
  index: number;
  schedule: Schedule;
};

const ShareTableEmptyCell = () => {
  return (
    <Box css={styleShareTableEmptyChell}>
      <img
        src={`${GATSBY_CDN_URL}/infcon/infcon-2023/main-share-table-x.png`}
        aria-label="비어있는 시간표 셀"
      />
    </Box>
  );
};

const ShareTableCell = ({ index, schedule }: CellProps) => {
  const speakerName = useMemo(() => {
    const splitedSpeakerName = schedule.speakerName.split(/\s*,\s*/);

    if (splitedSpeakerName.length > 1) {
      return `${splitedSpeakerName[0]} 외`;
    }

    return schedule.speakerName;
  }, [schedule]);
  return (
    <Box
      p={6}
      css={styleShareTableCell(SHARE_TABLE_CELL_COLOR[index])}
      className="cell"
      key={`${schedule.sessionLocation}-${index}-cell`}>
      <Ellipsis lineClamp={3}>
        <Text
          color="white"
          size={10}
          weight="bold"
          css={{ lineHeight: 1.2 }}
          className="sessionTitle">
          {schedule.title}
        </Text>
      </Ellipsis>
      <Box
        css={[styleShareTableCellInstructor, { borderColor: SHARE_TABLE_CELL_COLOR[index] }]}
        className="speakerName">
        <Text color={SHARE_TABLE_CELL_COLOR[index]} css={styleShareTableCellInstructorText}>
          {speakerName}
        </Text>
      </Box>
    </Box>
  );
};

const ShareTableNetworkCell = () => {
  return (
    <Box css={styleShareTableNetworkCell}>
      <Icon icon={faComment} color="black" size={20} css={{ opacity: 0.6 }} />
      <Text size={10} color="black" weight="bold" css={{ opacity: 0.6 }} mt={4}>
        네트워킹
      </Text>
    </Box>
  );
};

const ShareTablePrepareNetworkCell = () => {
  return <Box css={styleShareTablePrepareNetworkCell}></Box>;
};

const styleShareTimeCell = css({
  padding: '14px 0',
  marginRight: '10px'
});

const styleShareTableCell = (color: string) =>
  css({
    overflow: 'hidden',
    position: 'relative',
    aspectRatio: '1 / 1',
    backgroundColor: color,
    boxSizing: 'border-box',
    padding: '6px 6px 18px 6px',
    boxShadow:
      '0px 0.48px 2.02px -0.83px rgba(0, 0, 0, 0.13), 0px 1.83px 7.69px -1.67px rgba(0, 0, 0, 0.125), 0px 8px 33.6px -2.5px rgba(0, 0, 0, 0.125)',
    alignContent: 'flex-start',
    flexWrap: 'nowrap',
    borderRadius: 8,
    width: '100%',
    minWidth: 0
  });

const styleShareTableEmptyChell = css({
  position: 'relative',
  aspectRatio: '1 / 1',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  img: {
    width: '100%',
    height: '100%'
  }
});

const styleShareTableStartedAt = css({
  fontSize: '14px',
  color: '#333333',
  fontWeight: '900',
  marginBottom: '6px',
  lineHeight: '1'
});

const styleShareTableEndedAt = css({
  fontSize: '12px',
  color: 'black',
  opacity: 0.4,
  lineHeight: '1',
  fontWeight: '600'
});

const styleShareTableCellInstructor = css({
  position: 'absolute',
  bottom: 0,
  right: 0,
  padding: '2px 3px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: '50px',
  backgroundColor: 'white',
  transformOrigin: 'right bottom'
});

const styleShareTableCellInstructorText = css({
  fontSize: '8px',
  lineHeight: 1,
  fontWeight: '900',
  wordBreak: 'break-all',
  maxWidth: '37px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
});

export const styleShareTableNetworkCell = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gridArea: 'networking',
  width: '100%',
  backgroundColor: 'rgba(240, 242, 245, 0.7)',
  borderRadius: 8
});

export const styleShareTablePrepareNetworkCell = css({
  display: 'flex',
  gridArea: 'prepare-networking',
  boxSizing: 'border-box',
  overflow: 'hidden',
  opacity: 0.6,
  borderRadius: 8,
  border: '1px dashed #cbcccd'
});

export default ShareTableRow;
